import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const bgImage = process.env.PUBLIC_URL + '/japanesefishingvillaige.png';

function Register() {
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '', full_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const render = () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
          theme: 'dark',
        });
      }
    };

    if (window.turnstile) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the security check');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...requestData } = formData;
      await axios.post(`${API_URL}/auth/request-access`, {
        ...requestData,
        turnstileToken
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request. Please try again.');
      // Reset Turnstile on error so user can try again
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" style={{ backgroundImage: `url(${bgImage})` }}>
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src={process.env.PUBLIC_URL + '/pelicanplanner-logo.png'} alt="Pelican Planner"
            style={{
              width: '96px', height: '96px', margin: '0 auto 16px',
              borderRadius: '16px', display: 'block', objectFit: 'cover',
              boxShadow: '0 4px 16px rgba(255,107,107,0.3)'
            }} />
          <h1 className="auth-title">Request Access</h1>
          <p className="auth-subtitle">Submit your details — you'll be notified once approved</p>
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📬</div>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>Request Submitted!</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.6 }}>
              Your request has been sent for review. You'll receive an email at <strong>{formData.email}</strong> once your account is approved.
            </p>
            <p style={{ marginTop: '24px' }}>
              <Link to="/login" className="auth-link">Back to Sign In</Link>
            </p>
          </div>
        ) : (
          <>
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input type="text" name="full_name" className="form-input"
                  value={formData.full_name} onChange={handleChange}
                  placeholder="Your full name" />
              </div>

              <div className="form-group">
                <label className="form-label">Username *</label>
                <input type="text" name="username" className="form-input"
                  value={formData.username} onChange={handleChange} required
                  placeholder="Choose a username" />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input type="email" name="email" className="form-input"
                  value={formData.email} onChange={handleChange} required
                  placeholder="your@email.com" />
              </div>

              <div className="form-group">
                <label className="form-label">Password *</label>
                <input type="password" name="password" className="form-input"
                  value={formData.password} onChange={handleChange} required
                  minLength="6" placeholder="Min 6 characters" />
              </div>

              <div className="form-group">
                <label className="form-label">Confirm Password *</label>
                <input type="password" name="confirmPassword" className="form-input"
                  value={formData.confirmPassword} onChange={handleChange} required
                  minLength="6" placeholder="Re-enter password" />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}>
                  <div ref={turnstileRef}></div>
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}
                style={{ marginTop: '8px', padding: '12px' }}>
                {loading ? 'Submitting...' : 'Request Access'}
              </button>
            </form>

            <p className="text-center mt-3" style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">Sign In</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default Register;
